import {
  getSettings,
  updateSettings,
  getRateCardMapping,
  getRateCardMappingRow,
  getRateCardMappingRows,
  getRateCardMappingShortlist,
  getFieldsMapping,
  deleteFieldsMapping,
  updateFieldsMapping,
  getFieldsMappingDetail,
  lookupFieldsMapping,
  getJobCatalogMapping,
  getJobCatalogMappingRow,
  getJobCatalogTitleDescriptionRow,
  getCalculatedFields,
  getCalculatedFieldVisibility,
  updateCalculatedFieldVisibility
} from '@/services/settings-api.service.js'
import { buildAllowedValuesFromSources, fetchCustomFieldSyncInputs } from '@/lib/allowedValuesSync'
import { v4 as uuidv4 } from 'uuid'
import { logger } from '@/utils/logger'

const getDefaultState = () => {
  return {
    isLoaded: false,
    boardId: null,
    rootPersonIds: null,
    hrLeadPersonId: null,
    hiringRequestReviewers: [],
    planApprovers: null,
    performanceAccessibleByHROnly: false,
    baseCurrency: 'USD',
    fiscalMonth: 1,
    nextJobCode: '0',
    isRestrictToTeamViewEnabled: false,
    forecastSheetId: null,
    revenueAttributionData: null,
    isGoogleSheetsAutoSyncOn: false,
    isPlanStrictCheckEnabled: false,
    lockSubmittedPlan: false,
    formulas: [],
    reviewers: [],
    organizationUnits: {},
    fieldVisibility: {},
    hubTheme: null,
    stateOptions: {},
    rateCardAttributeColumns: [],
    rateCardMapping: {},
    rateCardSalaryColumn: null,
    fieldsMappingAttributeColumns: [],
    fieldsMappingList: [],
    fieldsMappingTargetColumn: null,
    fieldsMappingDetails: {}, // For shortlist calculation
    jobCatalogTitleColumn: null,
    jobCatalogMapping: {},
    jobCatalogDescriptionColumn: null,
    jobCatalogPivotColumn: null,
    jobCatalogAutofilledColumn: [],
    exportReports: [],
    calculatedFields: [], // List of toggleable calculated fields from backend
    calculatedFieldVisibility: {},
    _calculatedFieldIdsCache: new Set() // Cached Set for O(1) lookups
  }
}

const state = getDefaultState()

const getters = {
  isGoogleSheetsAutoSyncOn: (_state) => {
    return _state.isGoogleSheetsAutoSyncOn
  },
  preferredBaseCurrency: (_state) => {
    return _state.baseCurrency || 'USD'
  },
  preferredFiscalMonth: (_state) => {
    return _state.fiscalMonth || 1
  },
  loaded: (_state) => {
    return _state.isLoaded
  },
  nextJobCode: (_state) => {
    return _state.nextJobCode
  },
  rootPersonIds: (_state) => {
    return _state.rootPersonIds
  },
  forecastSheetId: (_state) => {
    return _state.forecastSheetId
  },
  revenueAttributionData: (_state) => {
    return _state.revenueAttributionData
  },
  planApprovers: (_state) => {
    return _state.planApprovers
  },
  isPlanStrictCheckEnabled: (_state) => {
    return _state.isPlanStrictCheckEnabled ?? false
  },
  isLockSubmittedPlanEnabled: (_state) => {
    return _state.lockSubmittedPlan ?? false
  },
  planApproversFlat: (_state) => {
    return Object.values(_state.planApprovers || {}).flat()
  },
  formulas: (_state) => {
    return _state.formulas || []
  },
  reviewers: (_state) => {
    return _state?.reviewers || []
  },
  planReviewers: (_state) => (boardId) => {
    return _state[boardId]?.reviewers || []
  },
  isReviewer:
    (_state) =>
    ({ uid, boardId }) =>
      _state.reviewers?.find((reviewer) => reviewer.uid === uid) ||
      _state[boardId]?.reviewers?.find((reviewer) => reviewer.uid === uid),
  organizationUnits: (_state) => {
    return _state.organizationUnits
  },
  getFieldVisibility: (_state) => {
    return _state.fieldVisibility
  },
  getHubTheme: (_state) => {
    return _state.hubTheme || {}
  },
  stateOptions: (_state) => {
    return _state.stateOptions || {}
  },
  rateCardMapping: (_state) => {
    return _state.rateCardMapping || {}
  },
  rateCardAttributeColumns: (_state) => {
    return _state.rateCardAttributeColumns || []
  },
  rateCardSalaryColumn: (_state) => {
    return _state.rateCardSalaryColumn || null
  },
  fieldsMappingList: (_state) => {
    return _state.fieldsMappingList || []
  },
  fieldsMappingAttributeColumns: (_state) => {
    return _state.fieldsMappingAttributeColumns || []
  },
  fieldsMappingTargetColumn: (_state) => {
    return _state.fieldsMappingTargetColumn || null
  },
  fieldsMappingDetails: (_state) => {
    return _state.fieldsMappingDetails || {}
  },
  jobCatalogMapping: (_state) => {
    return _state.jobCatalogMapping || {}
  },
  jobCatalogTitleColumn: (_state) => {
    return _state.jobCatalogTitleColumn || null
  },
  jobCatalogDescriptionColumn: (_state) => {
    return _state.jobCatalogDescriptionColumn || null
  },
  jobCatalogPivotColumn: (_state) => {
    return _state.jobCatalogPivotColumn || null
  },
  jobCatalogAutofilledColumn: (_state) => {
    return _state.jobCatalogAutofilledColumn || []
  },
  exportReports: (_state) => {
    return _state.exportReports || []
  },
  fieldMapping: (_state) => {
    return _state.fieldMapping || {}
  },
  getCalculatedFields: (_state) => {
    return _state.calculatedFields || []
  },
  getCalculatedFieldIds: (_state) => {
    // Return cached Set to avoid creating new Set on every access
    return _state._calculatedFieldIdsCache
  },
  getCalculatedFieldVisibility: (_state) => {
    return _state.calculatedFieldVisibility || {}
  },
  dayforceOnboardingCompleted: (_state) => {
    return _state.dayforceOnboardingCompleted === true
  }
}

const mutations = {
  setSettings(_state, settings) {
    if (!settings) return
    _state = Object.assign(_state, settings)
  },
  setPlanSettings(_state, { boardId, settings }) {
    if (!settings) return
    if (!_state[boardId]) {
      _state[boardId] = getDefaultState()
    }
    _state[boardId] = Object.assign(_state[boardId], settings)
  },
  resetSettings(_state) {
    Object.assign(_state, getDefaultState())
  },
  setForecastSheetId(_state, forecastSheetId) {
    _state.forecastSheetId = forecastSheetId
  },
  setPlanApprovers(_state, approvers) {
    _state.planApprovers = approvers
  },
  setPlanStrictCheck(_state, strictCheck) {
    _state.isPlanStrictCheckEnabled = strictCheck
  },
  setLockSubmittedPlan(_state, lockSubmittedPlan) {
    _state.lockSubmittedPlan = lockSubmittedPlan
  },
  setRevenueAttributionData(_state, revenueAttributionData) {
    _state.revenueAttributionData = revenueAttributionData
  },
  setIsLoaded(_state, isLoaded) {
    _state.isLoaded = isLoaded
  },
  addOrUpdateFormula(_state, formula) {
    if (_state.formulas === null) _state.formulas = []

    const index = _state.formulas.findIndex((f) => f.name === formula.name)
    if (index === -1) {
      _state.formulas.push(formula)
    } else {
      _state.formulas[index] = formula
    }
  },
  removeFormula(_state, formulaName) {
    const index = _state.formulas.findIndex((f) => f.name === formulaName)
    if (index !== -1) {
      _state.formulas.splice(index, 1)
    }
  },

  setReviewers(_state, reviewers) {
    _state.reviewers = reviewers
  },
  setOrganizationUnits(_state, organizationUnits) {
    _state.organizationUnits = organizationUnits
  },
  setFieldVisibility(_state, fieldVisibility) {
    _state.fieldVisibility = fieldVisibility
  },
  setHubColor(_state, { attribute, color }) {
    _state.hubTheme = state.hubTheme ?? {}
    _state.hubTheme[attribute] = color
  },
  setStateOptions(_state, stateOptions) {
    _state.stateOptions = stateOptions
  },
  setRateCardMapping(_state, rateCardMapping) {
    _state.rateCardMapping = rateCardMapping
  },
  setJobCatalogMapping(_state, jobCatalogMapping) {
    _state.jobCatalogMapping = jobCatalogMapping
  },
  setRateCardAttributeColumns(_state, rateCardAttributeColumns) {
    _state.rateCardAttributeColumns = rateCardAttributeColumns
  },
  setRateCardSalaryColumn(_state, rateCardSalaryColumn) {
    _state.rateCardSalaryColumn = rateCardSalaryColumn
  },
  setFieldsMappingList(_state, fieldsMappingList) {
    _state.fieldsMappingList = fieldsMappingList
  },
  setFieldsMappingAttributeColumns(_state, fieldsMappingAttributeColumns) {
    _state.fieldsMappingAttributeColumns = fieldsMappingAttributeColumns
  },
  setFieldsMappingTargetColumn(_state, fieldsMappingTargetColumn) {
    _state.fieldsMappingTargetColumn = fieldsMappingTargetColumn
  },
  setFieldsMappingDetail(_state, { mappingId, detail }) {
    _state.fieldsMappingDetails = {
      ..._state.fieldsMappingDetails,
      [mappingId]: detail
    }
  },
  clearFieldsMappingDetail(_state, mappingId) {
    if (!mappingId || !_state.fieldsMappingDetails?.[mappingId]) return
    const nextDetails = { ..._state.fieldsMappingDetails }
    delete nextDetails[mappingId]
    _state.fieldsMappingDetails = nextDetails
  },
  clearFieldsMappingDetails(_state) {
    _state.fieldsMappingDetails = {}
  },
  setJobCatalogTitleColumn(_state, jobCatalogTitleColumn) {
    _state.jobCatalogTitleColumn = jobCatalogTitleColumn
  },
  setJobCatalogDescriptionColumn(_state, jobCatalogDescriptionColumn) {
    _state.jobCatalogDescriptionColumn = jobCatalogDescriptionColumn
  },
  setJobCatalogPivotColumn(_state, jobCatalogPivotColumn) {
    _state.jobCatalogPivotColumn = jobCatalogPivotColumn
  },
  setJobCatalogAutofilledColumn(_state, jobCatalogAutofilledColumn) {
    _state.jobCatalogAutofilledColumn = jobCatalogAutofilledColumn
  },
  setExportReports(_state, exportReports) {
    _state.exportReports = exportReports
  },
  setFieldMapping(_state, fieldMapping) {
    _state.fieldMapping = fieldMapping
  },
  setCalculatedFields(_state, calculatedFields) {
    _state.calculatedFields = calculatedFields
    // Update cached Set for O(1) lookups
    _state._calculatedFieldIdsCache = new Set((calculatedFields || []).map((f) => f.id))
  },
  setCalculatedFieldVisibility(_state, calculatedFieldVisibility) {
    _state.calculatedFieldVisibility = calculatedFieldVisibility
  },
  setDayforceOnboardingCompleted(_state, completed) {
    _state.dayforceOnboardingCompleted = completed
  }
}

const actions = {
  resetSettings(context) {
    context.commit('resetSettings')
  },
  async setBaseCurrency(context, { baseCurrency, boardId }) {
    if (!boardId) throw new Error('BoardId is missing')
    try {
      await updateSettings({ boardId, settings: { baseCurrency } })
      context.commit('setSettings', { baseCurrency })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },
  async setFiscalMonth(context, { fiscalMonth, boardId }) {
    if (!boardId) throw new Error('BoardId is missing')
    try {
      await updateSettings({ boardId, settings: { fiscalMonth } })
      context.commit('setSettings', { fiscalMonth })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },
  async fetchPlanSettings(context, { boardId }) {
    try {
      const settings = await getSettings({ boardId })
      context.commit('setPlanSettings', { boardId, settings })
      return settings
    } catch (e) {
      if (!e.message.includes('Missing or insufficient permissions')) logger.error(e.message)
      return null
    }
  },
  async fetchSettings(context, { boardId }) {
    try {
      const settings = await getSettings({ boardId })
      context.commit('setSettings', settings)
    } catch (e) {
      if (!e.message.includes('Missing or insufficient permissions')) logger.error(e.message)
    } finally {
      context.commit('setIsLoaded', true)
    }
  },
  async setForecastSheetId(context, { forecastSheetId, boardId }) {
    try {
      context.commit('setForecastSheetId', forecastSheetId)

      await updateSettings({ boardId, settings: { forecastSheetId } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },
  async setPlanApprovers(context, { planApprovers, boardId }) {
    try {
      context.commit('setPlanApprovers', planApprovers)

      await updateSettings({ boardId, settings: { planApprovers } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong while setting approvers to board settings')
    }
  },
  async setPlanStrictCheck(context, { strickCheck, boardId }) {
    try {
      context.commit('setPlanStrictCheck', strickCheck)

      await updateSettings({ boardId, settings: { isPlanStrictCheckEnabled: strickCheck } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong while setting strict check to board settings')
    }
  },
  async toggleLockSubmittedPlan(context, { lockSubmittedPlan, boardId }) {
    try {
      context.commit('setLockSubmittedPlan', lockSubmittedPlan)

      await updateSettings({ boardId, settings: { lockSubmittedPlan } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong while setting lock scenarios to board settings')
    }
  },
  async setRevenueAttributionData(context, { revenueAttributionData, boardId }) {
    try {
      context.commit('setRevenueAttributionData', revenueAttributionData)
      await updateSettings({ boardId, settings: { revenueAttributionData } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },
  async setFormula(context, { formula, boardId }) {
    try {
      context.commit('addOrUpdateFormula', formula)
      await updateSettings({ boardId, settings: { formulas: context.getters.formulas } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },
  async removeFormula(context, { formulaName, boardId }) {
    try {
      context.commit('removeFormula', formulaName)
      await updateSettings({ boardId, settings: { formulas: context.getters.formulas } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },

  async saveGoogleAutoSyncSetting(context, { isGoogleSheetsAutoSyncOn, boardId }) {
    context.commit('isGoogleSheetsAutoSyncOn', isGoogleSheetsAutoSyncOn)
    await updateSettings({
      boardId,
      settings: { isGoogleSheetsAutoSyncOn: context.getters.isGoogleSheetsAutoSyncOn }
    })
  },

  async setReviewers(context, { reviewers, boardId }) {
    try {
      context.commit('setReviewers', reviewers)
      await updateSettings({ boardId, settings: { reviewers: context.getters.reviewers } })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },

  async setPlanReviewers(context, { reviewers, boardId }) {
    try {
      context.commit('setPlanReviewers', reviewers)
      await updateSettings({
        boardId,
        settings: { reviewers: context.getters.planReviewers(boardId) }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },

  async setOrganizationUnits(context, { organizationUnits, boardId }) {
    try {
      context.commit('setOrganizationUnits', organizationUnits)
      await updateSettings({
        boardId,
        settings: { organizationUnits: context.getters.organizationUnits }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong saving')
    }
  },

  async saveFieldVisibility(context, { fieldVisibility, boardId }) {
    try {
      context.commit('setFieldVisibility', fieldVisibility)
      await updateSettings({
        boardId,
        settings: { fieldVisibility: context.getters.getFieldVisibility }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving field visibility')
    }
  },

  async setHubColor(context, { boardId, attribute, color }) {
    context.commit('setHubColor', { attribute, color })

    await updateSettings({
      boardId,
      settings: { hubTheme: { ...context.getters.getHubTheme, [attribute]: color } }
    })
  },

  async setStateOptions(context, { stateOptions, boardId }) {
    try {
      context.commit('setStateOptions', stateOptions)
      await updateSettings({
        boardId,
        settings: { stateOptions: context.getters.stateOptions }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving state options')
    }
  },

  async setRateCardAttributeColumns(context, { rateCardAttributeColumns, boardId }) {
    try {
      context.commit('setRateCardAttributeColumns', rateCardAttributeColumns)
      await updateSettings({
        boardId,
        settings: { rateCardAttributeColumns: context.getters.rateCardAttributeColumns }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving rate card columns')
    }
  },

  async fetchRateCardMapping(context, { boardId }) {
    try {
      const rateCardMapping = await getRateCardMapping({ boardId })

      if (rateCardMapping?.success && rateCardMapping?.data?.columns) {
        rateCardMapping.data.rateCardMapping = rateCardMapping.data.rateCardMapping.map((row) => {
          return rateCardMapping.data.columns.reduce((orderedRow, column) => {
            orderedRow[column] = row[column]
            return orderedRow
          }, {})
        })
      }

      context.commit('setRateCardMapping', rateCardMapping)
    } catch (e) {
      logger.error(e.message)
    }
  },

  async fetchJobCatalogMapping(context, { boardId }) {
    try {
      const jobCatalogMapping = await getJobCatalogMapping({ boardId })

      if (jobCatalogMapping?.success && jobCatalogMapping?.data?.columns) {
        jobCatalogMapping.data.jobCatalogMapping = jobCatalogMapping.data.jobCatalogMapping.map(
          (row) => {
            return jobCatalogMapping.data.columns.reduce((orderedRow, column) => {
              orderedRow[column] = row[column]
              return orderedRow
            }, {})
          }
        )
      }

      context.commit('setJobCatalogMapping', jobCatalogMapping)
    } catch (e) {
      logger.error(e.message)
    }
  },

  async syncAllowedValuesFromSources(context, { boardId } = {}) {
    if (!boardId) {
      return context.rootGetters['meta/getAllowedValues'] || {}
    }

    try {
      const refreshTasks = [
        context.dispatch('meta/fetchAllowedValues', { boardId }, { root: true }),
        context.dispatch('meta/fetchPeopleMetaData', { boardId }, { root: true }),
        context.dispatch('fetchRateCardMapping', { boardId }),
        context.dispatch('fetchJobCatalogMapping', { boardId }),
        context.dispatch('fetchFieldsMapping', { boardId })
      ]

      await Promise.allSettled(refreshTasks)

      const currentAllowedValues = context.rootGetters['meta/getAllowedValues'] || {}
      const departments = context.rootGetters['meta/getDepartments'] || []
      const roles = context.rootGetters['meta/getRoles'] || []
      const locations = context.rootGetters['meta/getLocations'] || []

      const board = context.rootGetters.getBoard?.(boardId)
      const typesBoardId = board?.originalBoardId || boardId

      let customFieldSyncInputs
      try {
        customFieldSyncInputs = await fetchCustomFieldSyncInputs({
          boardId,
          typesBoardId,
          requireComplete: true
        })
      } catch (fetchError) {
        logger.error('Failed to fetch custom field sync inputs:', fetchError.message)
        return currentAllowedValues
      }
      const { customFieldTypes, uniqueCustomFieldValues } = customFieldSyncInputs

      const sourceRows = [
        context.getters.rateCardMapping?.data?.rateCardMapping || [],
        context.getters.jobCatalogMapping?.data?.jobCatalogMapping || []
      ]

      await context.dispatch('fetchFieldsMappingDetails', { boardId })

      const activeMappings = (context.getters.fieldsMappingList || []).filter(
        (mapping) => mapping?.id && mapping.isActive
      )

      const cachedDetails = context.getters.fieldsMappingDetails || {}
      for (const mapping of activeMappings) {
        const mappingDetail = cachedDetails[mapping.id]
        if (!mappingDetail) continue

        const mappingPayload = mappingDetail.mapping
        const mappingRows = Array.isArray(mappingPayload)
          ? mappingPayload
          : mappingPayload?.data || []
        sourceRows.push(mappingRows)
      }

      const updatedAllowedValues = buildAllowedValuesFromSources({
        currentAllowedValues,
        departments,
        roles,
        locations,
        customFieldTypes,
        uniqueCustomFieldValues,
        sourceRows
      })

      await context.dispatch(
        'meta/updateAllowedValues',
        { boardId, allowedValues: updatedAllowedValues },
        { root: true }
      )

      return updatedAllowedValues
    } catch (error) {
      logger.error('Failed syncing allowed values from source data:', error.message)
      return context.rootGetters['meta/getAllowedValues'] || {}
    }
  },

  async getRateCardMappingRow(_, { boardId, attributeColumnValues }) {
    try {
      const rateCardMapping = await getRateCardMappingRow({ boardId, attributeColumnValues })
      return rateCardMapping.data
    } catch (e) {
      logger.error(e.message)
    }
  },

  async getRateCardMappingRows(_, { boardId, planBoardId, lookups }) {
    try {
      return await getRateCardMappingRows({ boardId, planBoardId, lookups })
    } catch (e) {
      logger.error(e.message)
      return { success: false, results: [], message: e.message }
    }
  },

  async setRateCardSalaryColumn(context, { rateCardSalaryColumn, boardId }) {
    try {
      context.commit('setRateCardSalaryColumn', rateCardSalaryColumn)
      await updateSettings({
        boardId,
        settings: { rateCardSalaryColumn: context.getters.rateCardSalaryColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving rate card salary column')
    }
  },

  async setFieldsMappingAttributeColumns(context, { fieldsMappingAttributeColumns, boardId }) {
    try {
      context.commit('setFieldsMappingAttributeColumns', fieldsMappingAttributeColumns)
      await updateSettings({
        boardId,
        settings: { fieldsMappingAttributeColumns: context.getters.fieldsMappingAttributeColumns }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving fields mapping columns')
    }
  },

  async fetchFieldsMapping(context, { boardId }) {
    try {
      const response = await getFieldsMapping({ boardId })

      if (response?.success && response?.data?.mappings) {
        context.commit('setFieldsMappingList', response.data.mappings)
      } else {
        context.commit('setFieldsMappingList', [])
      }
    } catch (e) {
      logger.error(e.message)
      context.commit('setFieldsMappingList', [])
    }
  },

  async deleteFieldsMapping(context, { boardId, mappingId }) {
    try {
      const response = await deleteFieldsMapping({ boardId, mappingId })

      if (response?.success) {
        const currentList = context.getters.fieldsMappingList
        context.commit(
          'setFieldsMappingList',
          currentList.filter((item) => item.id !== mappingId)
        )
        context.commit('clearFieldsMappingDetail', mappingId)
      }

      return response
    } catch (e) {
      logger.error(e.message)
      return null
    }
  },

  async updateFieldsMapping(context, { boardId, mappingId, data }) {
    try {
      const response = await updateFieldsMapping({ boardId, mappingId, data })

      if (response?.success) {
        const currentList = context.getters.fieldsMappingList
        const updatedList = currentList.map((item) => {
          if (item.id === mappingId) {
            return { ...item, ...response.data }
          }
          return item
        })
        context.commit('setFieldsMappingList', updatedList)
        context.commit('clearFieldsMappingDetail', mappingId)
      }

      return response
    } catch (e) {
      logger.error(e.message)
      return null
    }
  },

  async getFieldsMappingDetail(_, { boardId, mappingId }) {
    try {
      const response = await getFieldsMappingDetail({ boardId, mappingId })
      return response
    } catch (e) {
      logger.error(e.message)
      return { success: false, message: e.message }
    }
  },

  async fetchFieldsMappingDetails(context, { boardId }) {
    try {
      const mappingList = context.getters.fieldsMappingList || []
      // Only fetch details for active mappings (inactive mappings won't be used)
      const activeMappings = mappingList.filter((m) => m?.id && m.isActive)

      const mappingsToFetch = activeMappings.filter(
        (mapping) => !context.state.fieldsMappingDetails[mapping.id]
      )

      if (mappingsToFetch.length === 0) {
        return
      }

      const detailResults = await Promise.allSettled(
        mappingsToFetch.map((mapping) => getFieldsMappingDetail({ boardId, mappingId: mapping.id }))
      )

      for (const [index, detailResult] of detailResults.entries()) {
        if (detailResult.status !== 'fulfilled') continue

        const response = detailResult.value
        if (response?.success && response?.data) {
          context.commit('setFieldsMappingDetail', {
            mappingId: mappingsToFetch[index].id,
            detail: response.data
          })
        }
      }
    } catch (e) {
      logger.error('Error fetching fields mapping details:', e.message)
    }
  },

  /**
   * Unified lookup - auto-discovers relevant mappings by boardId
   * @param {Object} params
   * @param {string} params.boardId - Board ID (can be scenario)
   * @param {Object} params.lookup - { peopleIds: [...], changedField: { name, value } }
   * @returns {Promise<Object>} { success: true, results: [{ personId, mappings: [{ mappingId, data }] }] }
   */
  async lookupFieldsMapping(_, { boardId, lookup }) {
    try {
      const response = await lookupFieldsMapping({ boardId, lookup })
      return response
    } catch (e) {
      logger.error(e.message)
      return { success: false, message: e.message }
    }
  },

  async setFieldsMappingTargetColumn(context, { fieldsMappingTargetColumn, boardId }) {
    try {
      context.commit('setFieldsMappingTargetColumn', fieldsMappingTargetColumn)
      await updateSettings({
        boardId,
        settings: { fieldsMappingTargetColumn: context.getters.fieldsMappingTargetColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving fields mapping target column')
    }
  },

  async setJobCatalogTitleColumn(context, { jobCatalogTitleColumn, boardId }) {
    try {
      context.commit('setJobCatalogTitleColumn', jobCatalogTitleColumn)
      await updateSettings({
        boardId,
        settings: { jobCatalogTitleColumn: context.getters.jobCatalogTitleColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving job catalog columns')
    }
  },

  async setJobCatalogDescriptionColumn(context, { jobCatalogDescriptionColumn, boardId }) {
    try {
      context.commit('setJobCatalogDescriptionColumn', jobCatalogDescriptionColumn)
      await updateSettings({
        boardId,
        settings: { jobCatalogDescriptionColumn: context.getters.jobCatalogDescriptionColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving job catalog salary column')
    }
  },

  async setJobCatalogPivotColumn(context, { jobCatalogPivotColumn, boardId }) {
    try {
      context.commit('setJobCatalogPivotColumn', jobCatalogPivotColumn)
      await updateSettings({
        boardId,
        settings: { jobCatalogPivotColumn: context.getters.jobCatalogPivotColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving job catalog pivot column')
    }
  },

  async setJobCatalogAutofilledColumn(context, { jobCatalogAutofilledColumn, boardId }) {
    try {
      context.commit('setJobCatalogAutofilledColumn', jobCatalogAutofilledColumn)
      await updateSettings({
        boardId,
        settings: { jobCatalogAutofilledColumn: context.getters.jobCatalogAutofilledColumn }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving job catalog autofilled column')
    }
  },

  async getRateCardMappingShortlist(context, { boardId, shortlistBy }) {
    try {
      const rateCardMappingShortlist = await getRateCardMappingShortlist({
        boardId,
        shortlistBy,
        rateCardAttributeColumns: context.getters.rateCardAttributeColumns
      })

      return rateCardMappingShortlist?.data || {}
    } catch (e) {
      logger.error(e.message)
    }
  },

  async getJobCatalogMappingRow(_, { boardId, pivotValue }) {
    try {
      const jobCatalogMapping = await getJobCatalogMappingRow({ boardId, pivotValue })
      return jobCatalogMapping.data
    } catch (e) {
      logger.error(e.message)
    }
  },

  async getJobCatalogTitleDescriptionRow(_, { boardId, pivotColumnValue, jobTitleColumnValue }) {
    try {
      const jobCatalogTitleDescriptionRow = await getJobCatalogTitleDescriptionRow({
        boardId,
        pivotColumnValue,
        jobTitleColumnValue
      })
      return jobCatalogTitleDescriptionRow?.data
    } catch (e) {
      // Error is already handled in the API service, just return undefined
    }
  },

  async addExportReport(context, { name, selectedFields, boardId }) {
    try {
      const exportReports = context.getters.exportReports
      exportReports.push({ name, selectedFields, id: uuidv4() })

      context.commit('setExportReports', exportReports)
      await updateSettings({
        boardId,
        settings: { exportReports }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving export reports')
    }
  },

  async deleteExportReport(context, { id, boardId }) {
    const exportReports = context.getters.exportReports
    const updatedExportReports = exportReports.filter((report) => report.id !== id)
    context.commit('setExportReports', updatedExportReports)
    await updateSettings({ boardId, settings: { exportReports: updatedExportReports } })
  },

  async setFieldMapping(context, { fieldMapping, boardId }) {
    try {
      context.commit('setFieldMapping', fieldMapping)
      await updateSettings({
        boardId,
        settings: { fieldMapping: context.getters.fieldMapping }
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving field mapping')
    }
  },

  async setPlanSettings(context, { boardId, settings }) {
    try {
      context.commit('setPlanSettings', { boardId, settings })
      await updateSettings({ boardId, settings })
      return settings
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving plan settings')
    }
  },

  async fetchCalculatedFields(context) {
    // Only fetch once - these don't change at runtime
    if (context.state.calculatedFields.length > 0) {
      return context.state.calculatedFields
    }
    try {
      const calculatedFields = await getCalculatedFields()
      context.commit('setCalculatedFields', calculatedFields)
      return calculatedFields
    } catch (e) {
      logger.error(e.message)
      return []
    }
  },

  async fetchCalculatedFieldVisibility(context, { boardId }) {
    try {
      const calculatedFieldVisibility = await getCalculatedFieldVisibility({ boardId })
      context.commit('setCalculatedFieldVisibility', calculatedFieldVisibility)
      return calculatedFieldVisibility
    } catch (e) {
      logger.error(e.message)
      return {}
    }
  },

  async saveCalculatedFieldVisibility(
    context,
    { calculatedFieldVisibility, changedFields, boardId }
  ) {
    try {
      context.commit('setCalculatedFieldVisibility', calculatedFieldVisibility)
      await updateCalculatedFieldVisibility({
        boardId,
        calculatedFieldVisibility,
        changedFields
      })
    } catch (e) {
      logger.error(e.message)
      throw new Error('Something went wrong when saving calculated field visibility')
    }
  },

  setDayforceOnboardingCompleted(context, completed) {
    context.commit('setDayforceOnboardingCompleted', completed)
  }
}

export default {
  state,
  mutations,
  getters,
  actions,
  modules: {}
}
